<script setup lang="ts">
import { ref, computed } from 'vue';
import { usePromosStore } from '@/store/PromosStore';
import { PromosEnum } from '@/store/enums/PromosEnum';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { handleBeforeSendImage, handleSelectFile } from '@/utils/helper';
import FileUpload from 'primevue/fileupload';
import PreviewImgUpload from './HomePromoAdmin/PreviewImgUpload.vue';

interface Props {
  listUrlPreview: any;
  disabled?: boolean;
}
const props = defineProps<Props>();

const emits = defineEmits<{
  handleUploadData: [dataImage: any];
  handleRemoveImgReview: [key: any];
  'update:listUrlPreview': [value: any];
}>();

const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const promosStore = usePromosStore();
const fileUploadDesktop = ref();
const fileUploadMobile = ref();
const fileUploadTablet = ref();
const fileUploadWidescreen = ref();

const dataImages = computed({
  get() {
    return props.listUrlPreview;
  },
  set(value) {
    emits('update:listUrlPreview', value);
  }
});

const resetFileUpload = () => {
  fileUploadDesktop.value.remove();
  fileUploadDesktop.value.removeUploadedFile();
  fileUploadMobile.value.remove();
  fileUploadMobile.value.removeUploadedFile();
  fileUploadTablet.value.remove();
  fileUploadTablet.value.removeUploadedFile();
  fileUploadWidescreen.value.remove();
  fileUploadWidescreen.value.removeUploadedFile();
};

const handleRemoveFile = (screen: string, refInputUpload) => {
  if (refInputUpload) {
    refInputUpload.uploadedFileCount = 0;
  }

  dataImages.value[screen] = '';

  emits('handleUploadData', dataImages.value);
};

const handleUpload = (res, screen: string) => {
  let urlImg = '';
  if (res.xhr.status === 200) {
    urlImg = res.xhr.response;
  }
  dataImages.value[screen] = urlImg;

  emits('handleUploadData', dataImages.value);
};

defineExpose({
  resetFileUpload
});
</script>

<template>
  <div class="grid grid-nogutter surface-section text-800 mb-5 border-round top-banner-container">
    <div class="upload-wrapper">
      <div class="item-upload">
        <p class="mb-3 ml-2 title-screen">{{ $t('promos.labels.desktop') }}</p>
        <FileUpload
          name="file"
          ref="fileUploadDesktop"
          :url="promosStore.getUrlUploadImg"
          @upload="handleUpload($event, (screen = PromosEnum.screenURL.DESKTOP))"
          @removeUploadedFile="handleRemoveFile((screen = PromosEnum.screenURL.DESKTOP), (refInputUpload = fileUploadDesktop))"
          @select="handleSelectFile"
          @before-send="handleBeforeSendImage"
          :chooseLabel="$t('promos.labels.choose')"
          :uploadLabel="$t('promos.labels.upload')"
          :cancelLabel="$t('promos.labels.cancel')"
          accept="image/*"
          :maxFileSize="5000000"
          :invalid-file-size-message="$t('notification.validSizeImg')"
          :file-limit="1"
          :disabled="!authorizations?.configurationPage?.promos_edit || disabled"
        >
          <template #empty>
            <PreviewImgUpload v-if="listUrlPreview?.desktop" :urlImage="listUrlPreview?.desktop" @handleDeleteImage="emits('handleRemoveImgReview', PromosEnum.screenURL.DESKTOP)" />
            <p v-else>{{ $t('promos.labels.drag_file_desc') }}</p>
          </template>
        </FileUpload>
      </div>
      <div class="item-upload">
        <p class="mb-3 ml-2 title-screen">{{ $t('promos.labels.mobile') }}</p>
        <FileUpload
          name="file"
          ref="fileUploadMobile"
          :url="promosStore.getUrlUploadImg"
          @upload="handleUpload($event, (screen = PromosEnum.screenURL.MOBILE))"
          @removeUploadedFile="handleRemoveFile((screen = PromosEnum.screenURL.MOBILE), (refInputUpload = fileUploadMobile))"
          @select="handleSelectFile"
          @before-send="handleBeforeSendImage"
          :chooseLabel="$t('promos.labels.choose')"
          :uploadLabel="$t('promos.labels.upload')"
          :cancelLabel="$t('promos.labels.cancel')"
          accept="image/*"
          :maxFileSize="5000000"
          :invalid-file-size-message="$t('notification.validSizeImg')"
          :file-limit="1"
          :disabled="!authorizations?.configurationPage?.promos_edit || disabled"
        >
          <template #empty>
            <PreviewImgUpload v-if="listUrlPreview?.mobile" :urlImage="listUrlPreview?.mobile" @handleDeleteImage="emits('handleRemoveImgReview', PromosEnum.screenURL.MOBILE)" />
            <p v-else>{{ $t('promos.labels.drag_file_desc') }}</p>
          </template>
        </FileUpload>
      </div>
      <div class="item-upload">
        <p class="mb-3 ml-2 title-screen">{{ $t('promos.labels.tablet') }}</p>
        <FileUpload
          name="file"
          ref="fileUploadTablet"
          :url="promosStore.getUrlUploadImg"
          @upload="handleUpload($event, (screen = PromosEnum.screenURL.TABLET))"
          @removeUploadedFile="handleRemoveFile((screen = PromosEnum.screenURL.TABLET), (refInputUpload = fileUploadTablet))"
          @select="handleSelectFile"
          @before-send="handleBeforeSendImage"
          :chooseLabel="$t('promos.labels.choose')"
          :uploadLabel="$t('promos.labels.upload')"
          :cancelLabel="$t('promos.labels.cancel')"
          accept="image/*"
          :maxFileSize="5000000"
          :invalid-file-size-message="$t('notification.validSizeImg')"
          :file-limit="1"
          :disabled="!authorizations?.configurationPage?.promos_edit || disabled"
        >
          <template #empty>
            <PreviewImgUpload v-if="listUrlPreview?.tablet" :urlImage="listUrlPreview?.tablet" @handleDeleteImage="emits('handleRemoveImgReview', PromosEnum.screenURL.TABLET)" />
            <p v-else>{{ $t('promos.labels.drag_file_desc') }}</p>
          </template>
        </FileUpload>
      </div>
      <div class="item-upload">
        <p class="mb-3 ml-2 title-screen">{{ $t('promos.labels.wide_screen') }}</p>
        <FileUpload
          name="file"
          ref="fileUploadWidescreen"
          :url="promosStore.getUrlUploadImg"
          @upload="handleUpload($event, (screen = PromosEnum.screenURL.WIDESCREEN))"
          @removeUploadedFile="handleRemoveFile((screen = PromosEnum.screenURL.WIDESCREEN), (refInputUpload = fileUploadWidescreen))"
          @select="handleSelectFile"
          @before-send="handleBeforeSendImage"
          :chooseLabel="$t('promos.labels.choose')"
          :uploadLabel="$t('promos.labels.upload')"
          :cancelLabel="$t('promos.labels.cancel')"
          accept="image/*"
          :maxFileSize="5000000"
          :invalid-file-size-message="$t('notification.validSizeImg')"
          :file-limit="1"
          :disabled="!authorizations?.configurationPage?.promos_edit || disabled"
        >
          <template #empty>
            <PreviewImgUpload v-if="listUrlPreview?.widescreen" :urlImage="listUrlPreview?.widescreen" @handleDeleteImage="emits('handleRemoveImgReview', PromosEnum.screenURL.WIDESCREEN)" />
            <p v-else>{{ $t('promos.labels.drag_file_desc') }}</p>
          </template>
        </FileUpload>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
:deep() {
  .p-fileupload-file-details {
    width: 100%;
  }
  .p-fileupload-file-name {
    width: 100%;
    word-break: break-all;
  }
  .p-fileupload-file-actions {
    margin: auto;
  }
  .p-fileupload-file-thumbnail {
    width: 100%;
  }
}
.upload-wrapper {
  display: flex;
  gap: 1.4rem;
  flex-wrap: wrap;
  justify-content: space-around;
}

.item-upload {
  margin-bottom: 2rem;
  max-width: 176px;
}
.title-screen {
  font-weight: 700;
}
.p-button.p-component {
  padding: 0.45rem 0.95rem;
}
</style>
